import React, { useState, useRef, useEffect } from 'react';
import { ThumbsUp, ThumbsDown, ChevronDown, X } from 'lucide-react';
import { Badge } from './badge';
import { cn } from '../../lib/utils';

interface StatusFilterProps {
  selected: string[];
  onChange: (selected: string[]) => void;
  className?: string;
  placeholder?: string;
}

const statusOptions = [
  { value: 'TRUE', label: 'Accepted', icon: ThumbsUp, color: 'text-galvanizing-green' },
  { value: 'FALSE', label: 'Rejected', icon: ThumbsDown, color: 'text-readable-red' },
  { value: 'EX', label: 'Exclamation', icon: null, color: 'text-[#B38600]' },
  { value: 'CROSS', label: 'Cross', icon: X, color: 'text-readable-red' },
  { value: 'NULL', label: '-', icon: null, color: 'text-heritage-grey' }
];

export function StatusFilter({
  selected,
  onChange,
  className,
  placeholder = "Select status"
}: StatusFilterProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (value: string) => {
    const isSelected = selected.includes(value);
    if (isSelected) {
      onChange(selected.filter((s) => s !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const handleUnselect = (value: string) => {
    onChange(selected.filter((s) => s !== value));
  };

  const getSelectedStatus = (value: string) => {
    return statusOptions.find(option => option.value === value);
  };

  return (
    <div className="relative" ref={containerRef}>
      <div
        className={cn(
          "relative flex w-full cursor-pointer items-center rounded-md border border-[#E2E5EA] bg-white px-3 py-2 text-[13px] text-text-grey transition-colors hover:border-heritage-grey focus:outline-none focus:ring-2 focus:ring-hero-red/30",
          selected.length > 0 ? "min-h-9 h-auto" : "h-9",
          open && "border-hero-red ring-2 ring-hero-red/30",
          className
        )}
        onClick={() => setOpen(!open)}
      >
        <div className={cn(
          "flex min-w-0 flex-1 items-center gap-1",
          selected.length > 0 ? "flex-wrap" : "flex-nowrap"
        )}>
          {selected.length > 0 ? (
            selected.map((option) => {
              const selectedStatus = getSelectedStatus(option);
              if (!selectedStatus) return null;
              return (
                <Badge
                  key={option}
                  variant="secondary"
                  className="rounded-md border border-transparent bg-[rgba(0,39,63,0.06)] px-1.5 py-0.5 font-normal text-blueprint-blue transition-colors hover:border-hero-red-20 hover:bg-hero-red-10 hover:text-readable-red"
                >
                  <div className="flex items-center gap-1">
                    {selectedStatus.icon ? (
                      <selectedStatus.icon className={cn("h-3 w-3", selectedStatus.color)} />
                    ) : selectedStatus.value === 'EX' ? (
                      <span className="h-3 w-3 flex items-center justify-center font-bold text-xs bg-[rgba(230,161,0,0.08)] text-[#B38600] rounded-full">!</span>
                    ) : selectedStatus.value === 'CROSS' ? (
                      <span className="h-3 w-3 flex items-center justify-center font-bold text-xs text-readable-red">×</span>
                    ) : selectedStatus.value === 'NULL' ? (
                      <span className={cn("h-3 w-3 flex items-center justify-center font-bold text-xs", selectedStatus.color)}>-</span>
                    ) : null}
                    <span>{selectedStatus.label}</span>
                  </div>
                  <button
                    className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-hero-red focus:ring-offset-1"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleUnselect(option);
                      }
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUnselect(option);
                    }}
                    aria-label={`Remove ${selectedStatus.label}`}
                  >
                    <X className="h-3 w-3 text-heritage-grey hover:text-readable-red" />
                  </button>
                </Badge>
              );
            })
          ) : (
            <span className="truncate text-heritage-grey">{placeholder}</span>
          )}
        </div>
        <ChevronDown className={cn(
          "ml-2 h-4 w-4 shrink-0 text-heritage-grey transition-transform duration-200",
          open && "rotate-180"
        )} />
      </div>
      
      {open && (
        <div 
          className="absolute z-50 mt-1 w-full rounded-md border border-[#E2E5EA] bg-white text-text-grey shadow-lg outline-none animate-in"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="max-h-[200px] overflow-auto">
            {statusOptions.map((option) => {
              const isSelected = selected.includes(option.value);
              
              return (
                <div
                  key={option.value}
                  className={cn(
                    "flex cursor-pointer items-center gap-2 px-2 py-1.5 text-[13px] text-text-grey transition-colors",
                    "hover:bg-page-bg"
                  )}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSelect(option.value);
                  }}
                >
                  <div
                    className={cn(
                      "flex h-4 w-4 items-center justify-center rounded-sm border border-[#E2E5EA]",
                      isSelected
                        ? "bg-hero-red border-hero-red text-white"
                        : "bg-white"
                    )}
                  >
                    {isSelected && (
                      <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  {option.icon ? (
                    <option.icon className={cn("h-4 w-4", option.color)} />
                  ) : option.value === 'EX' ? (
                    <span className="h-4 w-4 flex items-center justify-center font-bold text-sm bg-[rgba(230,161,0,0.08)] text-[#B38600] rounded-full">!</span>
                  ) : option.value === 'CROSS' ? (
                    <span className="h-4 w-4 flex items-center justify-center font-bold text-sm text-readable-red">×</span>
                  ) : option.value === 'NULL' ? (
                    <span className={cn("h-4 w-4 flex items-center justify-center font-bold text-sm", option.color)}>-</span>
                  ) : null}
                  <span>{option.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
} 